import {
  getFileType,
  capitalizeFirstLetter,
  getFileIconPath,
  getDisplayFileName,
  getDocClassCodeOnly,
  isWorkingDocumentCheckedOut,
  isDocumentReleased,
} from "../../../../functions/helperMethod";
import {
  DOCUMENT_MODE,
  FILE_STATE,
  STATE,
  IFS_ACCESS_LEVEL,
} from "../../../../redux/constants/metaData";
import { getDocumentClassFormatLabel } from "../../../../functions/getDocumentClassFormatLabel";
import { resolveLastAction } from "../../../../functions/resolveDocumentListLastAction";

function getCheckoutAccessLevel(accessLevel) {
  switch ((accessLevel || "").toUpperCase()) {
    case IFS_ACCESS_LEVEL.admin:
    case IFS_ACCESS_LEVEL.edit:
      return false;
    case IFS_ACCESS_LEVEL.view:
    case IFS_ACCESS_LEVEL.noAccess:
    case IFS_ACCESS_LEVEL.noData:
    default:
      return true;
  }
}

function getPreviewAccessLevel(accessLevel) {
  switch ((accessLevel || "").toUpperCase()) {
    case IFS_ACCESS_LEVEL.admin:
    case IFS_ACCESS_LEVEL.edit:
    case IFS_ACCESS_LEVEL.view:
      return false;
    case IFS_ACCESS_LEVEL.noAccess:
    case IFS_ACCESS_LEVEL.noData:
    default:
      return true;
  }
}

function getIsAccessible(accessLevel) {
  switch ((accessLevel || "").toUpperCase()) {
    case IFS_ACCESS_LEVEL.admin:
    case IFS_ACCESS_LEVEL.edit:
    case IFS_ACCESS_LEVEL.view:
      return true;
    case IFS_ACCESS_LEVEL.noAccess:
    case IFS_ACCESS_LEVEL.noData:
    default:
      return false;
  }
}

function getCheckedOutStatus(fileState) {
  return fileState === FILE_STATE.checkedOut;
}

function getApprovedStatus(docState) {
  return docState === STATE.approved;
}

function firstNonEmpty(...values) {
  for (const value of values) {
    if (value == null) continue;
    const text = String(value).trim();
    if (text !== "") return text;
  }
  return "";
}

/**
 * Maps IFS document list + working document list into table row items for the All Documents grid.
 * @param {Array} documents - Raw document list from search/filter
 * @param {Array} [workingDocumentList] - Working documents (OneDrive) for matching
 * @param {Object} [attachedDocInfo] - Recently checked-in doc for "new" badge
 * @returns {Array} Row items for DetailsList
 */
export function getDocumentTableItems(
  documents = [],
  workingDocumentList = [],
  attachedDocInfo = null,
  docClassFormatList = []
) {
  const recentDoc = attachedDocInfo;
  if (!documents || !Array.isArray(documents)) return [];

  const mappedItems = documents.map((item) => {
    const docClassCode = firstNonEmpty(item.docClass, item.DocClass, item.class, item.Class);
    const formatSizeName = firstNonEmpty(
      item.FormatSizeName,
      item.formatSizeName,
      item.formatName,
      item.FormatName,
      item.DocFormatDescription,
      item.docFormatDescription
    );
    let classDisplayName = firstNonEmpty(item.DocClassName, item.docClassName);
    if (!classDisplayName && docClassCode && Array.isArray(docClassFormatList)) {
      const classItem = docClassFormatList.find((x) => {
        const code = firstNonEmpty(x?.docClass, x?.DocClass, x?.class, x?.Class);
        return code === docClassCode;
      });
      classDisplayName = firstNonEmpty(
        classItem?.docName,
        classItem?.DocName,
        classItem?.name,
        classItem?.Name
      );
    }
    const classOrFormatDisplayName = firstNonEmpty(
      formatSizeName,
      classDisplayName
    );
    const formatSizeCode = firstNonEmpty(
      item.FormatSize,
      item.formatSize,
      item.format,
      item.Format
    );
    let resolvedFormatName = classOrFormatDisplayName;
    if (!resolvedFormatName && docClassCode && formatSizeCode && Array.isArray(docClassFormatList)) {
      const classItem = docClassFormatList.find((x) => {
        const code = firstNonEmpty(x?.docClass, x?.DocClass, x?.class, x?.Class);
        return code === docClassCode;
      });
      const formats = classItem?.formats ?? classItem?.Formats ?? [];
      if (Array.isArray(formats)) {
        const formatItem = formats.find((f) => {
          const code = firstNonEmpty(f?.formatSize, f?.FormatSize, f?.format, f?.Format);
          return code === formatSizeCode;
        });
        resolvedFormatName = firstNonEmpty(
          formatItem?.formatSizeName,
          formatItem?.FormatSizeName,
          formatItem?.formatName,
          formatItem?.FormatName,
          classDisplayName
        );
      }
    }

    const isRecentCheckIn =
      recentDoc &&
      getDocClassCodeOnly(docClassCode) === getDocClassCodeOnly(recentDoc.docClass) &&
      item.docNo === recentDoc.docNo &&
      item.docRev === recentDoc.docRev &&
      item.docSheet === recentDoc.docSheet;


    const isDownloadDisable = getCheckoutAccessLevel(item.AccessLevel);
    const iconPath = getFileIconPath(item.FileType, item);
    const lastActionInfo = resolveLastAction(item);

    return {
      key: item.Id,
      iconName: iconPath,
      documentNo: item.docNo + " - " + item.docRev,
      title: getDisplayFileName(item.Title ?? ""),
      documentClassFormat: getDocumentClassFormatLabel(
        docClassCode,
        resolvedFormatName
      ),
      documentBasic: `${docClassCode} - ${item.docNo} - ${item.docSheet} - ${item.docRev}`,
      titleInCapitalized: capitalizeFirstLetter(getDisplayFileName(item.Title ?? "")),
      checkedInDate: new Date(item.CheckedInDate).toLocaleDateString(),
      checkedOutDate: new Date(item.CheckedOutDate).toLocaleDateString(),
      fileSize: 0,
      sizeInbytes: item.FileSize,
      owner: item.DocRespName,
      class: docClassCode,
      className: classDisplayName,
      format: formatSizeCode,
      formatName: resolvedFormatName,
      sheetNo: item.docSheet,
      state: item.State,
      fileState: item.FileState,
      status: item.Status,
      docNo: item.docNo,
      docRev: item.docRev,
      docType: item.DocType,
      fileNo: item.FileNo,
      fileExtension: item.FileExtension,
      fileType: getFileType(item.FileExtension, item.FileType),
      lastActionDate: lastActionInfo.display,
      lastActionTimestamp: lastActionInfo.timestamp,
      lastAction: item.LastAction,
      viewStatus: item.ViewStatus,
      viewFileType: item.ViewFileType,
      ViewIconName:
        item.ViewFileExtension != null
          ? getFileIconPath(item.ViewFileType, {
              FileType: item.ViewFileType,
              FileExtension: item.ViewFileExtension,
              FileState: item.FileState,
              Status: item.Status,
              State: item.State,
            })
          : null,
      viewFileExtension: item.ViewFileExtension,
      documentMode: DOCUMENT_MODE.ifs,
      isCheckoutDisable:
        getCheckoutAccessLevel(item.AccessLevel) ||
        isDocumentReleased(item) ||
        getApprovedStatus(item.Status) ||
        getApprovedStatus(item.State),
      isCheckedOut:
        getCheckedOutStatus(item.FileState) || isWorkingDocumentCheckedOut(item),
      isReleased: isDocumentReleased(item),
      isPreviewDisable: getPreviewAccessLevel(item.AccessLevel),
      isDownloadDisable,
      isAccessible: getIsAccessible(item.AccessLevel),
      isRecentCheckIn,
    };
  });

  return mappedItems;
}
